//@ts-check
import * as THREE from "three";
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { Sky } from "three/examples/jsm/objects/Sky";
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { TWEEN } from "./tween/tween.module.min.js"
import * as lights from './utils/Lights.js'
import * as ground from './utils/Ground.js'
import * as selecTion from './utils/Selection.js'
import * as highLight from './utils/Highlight.js'
import * as toolTip from './utils/Tooltip.js'
import * as visibiliTy from './utils/Visibility.js'
import * as laBel from './utils/Label.js'
import * as cameras from './utils/Cameras.js'
import * as colorBy from './utils/ColorBy.js'
import * as materials from './utils/Materials.js'
import * as utils from './utils'
import * as clipping from './utils/Clipping.js'


/**
 * Default params
 * @typedef {Object} ViewerParams
 * @property {Number} [backgroundColor = 0xf9f9f9] - Background Color 
 * @property {Number} [horizonColor = 0x774ccff] - Horizon Color
 * @property {Number} [pickColor = 0x72aee6]  - Pick Color
 * @property {Number} [highlightColor = 0xff00ff] - Highlight Color
 * @property {Number} [ghostColor = 0xf3f3f3] - Ghost color 
 * @property {Number} [elevation = 20] - Elevation
 * @property {Number} [azimuth = 150] - Azimuth
 * @property {number} [sceneScale = 250000] - Scene Scale
 * @property {string} [groundColor = "#5c5858"] - Ground Color
 * @property {Number} [groundOpacity = 1] - Ground Opacity
 * @property {Number} [groundHeight= 0] - Ground Height
 * @property {Number} [groundScale = 1000] - Ground Scale
 * @property {boolean} [showGround = true] - Show Ground
 * @property {boolean} [showShadows = true] - Show Shadows
 * @property {boolean} [showSky = false] - Show Sky
 * @property {boolean} [showEdges = true] - Show Edges
 * @property {Number} [labelSize = 12] - Label Size
 * @property {Number} [digits = 4] - Digits
 * @property {Number} [tooltipColor = 0x000000] - Tooltip Color
 * @property {boolean} [animate = false] - Animate
 * 
 */

/**
 * Custom Label 
 * @typedef {Object} CustomLabel
 * @property {HTMLDivElement} content - HTML div element
 * @property {THREE.Object3D} element - THREE Object Element in the scene 
 * @property {CSS2DObject} label - three.js 2D HTML element 
 */


/**
 * Default viewer parameters 
 * @type {ViewerParams} 
 */
let _defaultParams = {
  backgroundColor: 0xf9f9f9,
  horizonColor: 0x774ccff,
  pickColor: 0x72aee6,
  highlightColor: 0xff00ff,
  ghostColor: 0xf3f3f3,
  elevation: 20,
  azimuth: 150,
  sceneScale: 250000,
  groundColor: "#5c5858",
  groundOpacity: 1,
  groundHeight: 0,
  groundScale: 1000,
  showGround: true,
  showShadows: true,
  showSky: false,
  showEdges: true,
  labelSize: 12,
  digits: 4,
  tooltipColor: 0x000000,
  animate: false
};

/**
 * Initates a new Viewer
 * @class
 */
class Viewer {

  /**
 * Observes changes to the size of the canvas element and adjusts the camera and renderer accordinly
 * @constant 
 * @type {ResizeObserver} 
 */
  resizeObserver;


  /**
   * 
   * @constructs Viewer
   * @param {ViewerParams} initParams 
   */
  constructor(initParams) {
    let params = { ..._defaultParams, ...(initParams || {}) };
    this.name = "";
    this.canvas = null;
    this.scene = null;
    this.modelScene = null;
    this.camera = null; 
    this.renderer = null;
    this.labelRenderer = null;
    this.orbitControls = null;
    this.mouse = null;
    this.canvasBounds = null;
    this.boundingBox = null;
    this.boundingSphere = null;
    this.multipleSelectionFlagCheck = null;
    this.intersected = null;
    this.lastSelectedElement = null;
    this.highlightElementFlagCheck = null;
    this.composer = null;
    this.renderPass = null;
    this.outlinePass = null;
    this.backgroundColor = params.backgroundColor;
    this.horizonColor = params.horizonColor;
    this.pickColor = params.pickColor;
    this.highlightColor = params.highlightColor;
    this.ghostColor = params.ghostColor;
    this.model = new Set(); //Copy of original model elements for material restores
    this.modelMap = new Map(); //Map of original model elements for material restores.
    this.elementsMap = new Map();//Map of scene Mesh elements by reference for picking.
    this.edgesMap = new Map();
    this.edgesGroup = new THREE.Group();
    this.intersectedArray = [];
    this.highLightArray = []; //List of scene model elements by reference for those currently highlighted .
    this.raycaster = null;
    this.tooltip = null;
    this.tooltipColor = params.tooltipColor;
    this.tooltipContent = 'none';
    /**
     * @type {Array<CustomLabel>}
     */
    this.labelVariables = [];
    this.labelSize = params.labelSize;
    this.digits = params.digits;
    this.elevation = params.elevation;
    this.azimuth = params.azimuth;
    this.sceneScale = params.sceneScale;
    this.lights = [];
    this.isModelDisposed = false;
    this.ground = null;
    this.defaultGroundPosition = null;
    this.groundColor = params.groundColor;
    this.groundOpacity = params.groundOpacity;
    this.groundHeight = params.groundHeight;
    this.groundScale = params.groundScale;
    this.showGround = params.showGround;
    this.showShadows = params.showShadows;
    this.showSky = params.showSky;
    this.showEdges = params.showEdges;
    this.sunlight = null;
    this.near = 1;
    this.selectionMaterial = null;
    this.highlightMaterial = null;
    this.ghostMaterial = null;
    this.opacityTempMaterial = null;
    this.edgeMaterial = new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 2 });
    this.groundMaterial = null;
    this.animate = params.animate;

    // Clipping variables
    this.collectionOfAllModelStnecilObjectAndPoGroup = new Map();

    this.clipActive = false;
    this.clipLocal = true; // if false global
    this.stencilCap = false;
    this.clipIntersection = false; // Only when when clipLocal is true 
    this.clipShadows = true; // Only when when clipLocal is true 
    this.clippingTypes= ['plane', 'box'];
    this.clippingType =  this.clippingTypes[0];
    this.clipDirections =  [{
      label: "x",
      key: "x",
      value: 0,
      active:  true,
      flip: true,
      valueRange: [0 ,1], // box parameters
      min: 0,
      max: 1
    }, {
      label: "y", 
      key: "y",
      value: 0,
      active:  true,
      flip: true,
      valueRange: [0 ,1], 
      min: 0,
      max: 1
    }, {
      label: "z",
      key: "z",
      value: 0,
      active:  true,
      flip: true,
      valueRange: [0 ,1], 
      min: 0,
      max: 1
    }];
    this.clippingPlanes = [];
    this.perspectiveCamera = null;
  }


  /**
   * Add viewer
   * @param {HTMLDivElement} canvas - The canvas element to add the viewer  
   * @param {string} name - id 
   * @returns {Object} Return success or error messages 
   */
  addViewer(canvas, name) {
    var scope = this;
    try {
      if ((canvas != undefined || canvas != null) && (name != undefined || name != null)) {
        this.name = name;
        this.canvas = canvas;

        // scene
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(this.backgroundColor);
        this.modelScene = new THREE.Object3D();
        this.scene.add(this.modelScene);
        // perspective camera
        this.camera = new THREE.PerspectiveCamera(50, canvas.offsetWidth / canvas.offsetHeight, this.near, 100000);
        this.camera.position.y = 100;
        this.camera.up.set(0, 0, 1);
        this.camera.lookAt(0, 0, 0);
        // renderer
        this.renderer = new THREE.WebGLRenderer(
          { antialias: true, 
            powerPreference: "default",
            //stencil: true 
          });
        this.renderer.setSize(canvas.offsetWidth, canvas.offsetHeight);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.outputColorSpace = THREE.SRGBColorSpace; 
        this.renderer.toneMappingExposure = 1.0;

        this.labelRenderer = new CSS2DRenderer();
        this.labelRenderer.setSize(canvas.offsetWidth, canvas.offsetHeight);
        this.labelRenderer.domElement.style.position = 'absolute';
        this.labelRenderer.domElement.style.top = '0px';

        // adding sky texture in scene
        this.sky = new Sky();
        this.sun = new THREE.Vector3();
        this.sky.scale.setScalar(this.sceneScale || 1);
        if (this.showSky) this.scene.add(this.sky);
        this.uniforms = this.sky.material.uniforms;
        this.uniforms["turbidity"].value = 10;
        this.uniforms["rayleigh"].value = 3;
        this.uniforms["mieCoefficient"].value = 0.05;
        this.uniforms["mieDirectionalG"].value = 0.999;
        this.uniforms.up.value.set(0, 0, 1);

        //set default materials
        this.setDefaultMaterials();

        //set default lights
        this.createDefaultLights();
        this.setLights();

        // set elevation and azimuth angles
        this.setSkyPosition();

        //set default ground
        this.createDefaultGround();

        // sun position calculations
        this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
        var texture = this.pmremGenerator.fromScene(this.sky).texture;
        this.scene.environment = texture;

        this.scene.background = new THREE.Color(this.backgroundColor);


        this.composer = new EffectComposer(this.renderer);
        this.canvas.appendChild(this.renderer.domElement);

        // this.labelRenderer.domElement.addEventListener("mouseup", onMouseUp, false);
        this.canvas.appendChild(this.labelRenderer.domElement);

        // orbit controls
        this.orbitControls = new OrbitControls(this.camera, this.labelRenderer.domElement);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.mouse = new THREE.Vector2();

        this.canvas.addEventListener("mousedown", (e) => {
          this.mouseDownEvent(e);
        }, false);
        
        //tooltip
        this.formatTooltipDefault(this.canvas);
        this.perspectiveCamera = this.camera;
        var returnValue = {
          "status": "success",
          "message": "Viewer created successfully."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Canvas or viewer name is either undefined or null."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "error: " + err.message
      }
      return returnValue;
    }
  }

  /**
   * Mouse down event listener
   * @param {UIEvent} event 
   */
  mouseDownEvent(event) {
    var scope = this;
    scope.isDragged = false;
  }


  /**
   * Registers UI events 
   * @param {*} eventName 
   * @param {*} callBack 
   * @returns {Object} Return success or error messages 
   */
  registerEvent(eventName, callBack) {
    var scope = this;
    try {
      if ((eventName != undefined || eventName != null) && (callBack != undefined || callBack != null)) {
        switch (eventName) {
          case "element-select": scope.canvas.addEventListener("click", (e) => scope.pickElement(e, callBack), false);
            break;
          case "element-highlight": scope.canvas.addEventListener("mousemove", (e) => scope.highlightElement(e, callBack), false);
            break;
        }
        var returnValue = {
          "status": "success",
          "message": "Event registered on viewer successfully."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Event name or callback is either undefined or null."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "Error: " + err.message
      }
      return returnValue;
    }
  }

  /**
   * Updates phi
   */
  updatePhi() {
    var scope = this;
    scope.phi = Math.PI * (scope.elevation / 180.0 - 0.5);
  }
  /**
  * Updates theta
  */
  updateTheta() {
    var scope = this;
    scope.theta = Math.PI * (scope.azimuth / 180.0);
  }
  /**
   * Updates Azimuth
   */
  updateAzimuth = utils.updateAzimuth;
  /**
   * Updates Elevation
   */
  updateElevation = utils.updateElevation;

  /**
   * Adds or Removes the Sky from the scene 
   * @param {boolean} status 
   */
  showSkybox(status) {
    this.showSky = status;
    if (this.showSky) {
      this.scene.add(this.sky);
    }
    else {
      this.scene.remove(this.sky);
    }
  }

  /**
   * Updates sky box scale
   * @param {boolean} update 
   * @returns {Object} Return success or error messages 
   */
  updateSkybox(update) {
    var scope = this;
    try {
      if (scope.sky != undefined || scope.sky != null) {
        if (update) {
          var box = new THREE.Box3();
          var targetModel = [];
          scope.elementsMap.forEach((child, name)=>{
            if (
              child instanceof THREE.Mesh &&
              !targetModel.includes(child.parent)
            ) {
              if(child.parent) {targetModel.push(child.parent); box.expandByObject(child.parent);}
            }
          })

          var size = box.getSize(new THREE.Vector3());
          var maxSize = Math.max(size.x, size.y, size.z) * 10000;
          scope.sky.scale.setScalar(maxSize);
        } else {
          scope.sky.scale.setScalar(scope.sceneScale);
        }
        //viewerMgrInstance.render();
        var returnValue = {
          "status": "success",
          "message": "Skybox size updated successfully."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Sky texture is not added in viewer or it is undefined."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "System Error: " +  err.message
      }
      return returnValue;
    }
  }

  /**
   * Sets scene Hemisphere light 
   * @param {Number} [intensity = 2] 
   * @returns {Object} Return success or error messages 
   */
  updateSceneIllumination(intensity = 2.0) {
    var scope = this;
    try {
      if (scope.scene != undefined || scope.scene != null) {
        var hemisphereLight = new THREE.HemisphereLight(
          scope.backgroundColor,
          scope.horizonColor,
          intensity
        );
        scope.scene.add(hemisphereLight);
        // var box = new THREE.Box3().setFromObject(scope.modelScene);
        // var center = box.getCenter(new THREE.Vector3());
        // var size = box.getSize(new THREE.Vector3());
        // var maxSize = Math.max(size.x, size.y, size.z) * 1.1;
        hemisphereLight.position.set(0, 0, 1);
        var returnValue = {
          "status": "success",
          "message": "Scene illumination added in viewer."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Scene is undefined or null."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "Sytstem Error " + err.message
      }
      return returnValue;
    }
  }
  /**
   * Set Scene Illumination
   * @param {Number} skyColor - Sky Color
   * @param {String} groundColor - Ground Color
   * @param {Number} intensity - Intensity
   */
  setSceneIllumination(skyColor, groundColor, intensity) {
    this.backgroundColor = skyColor;
    this.groundColor = groundColor;

    this.updateSceneIllumination(intensity);
  }

  // Lights
  // TODO JSdocs for the functions
  /**
   * @function createSunLight
   * @memberof Lights
   */
  createSunLight = lights.createSunLight;
  /**
   * @function createDefaultLights
   * @memberof Lights
   */
  createDefaultLights = lights.createDefaultLights;
  /**
   * @function setLights
   * @memberof Lights
   */
  setLights = lights.setLights;
  setLightShadows = lights.setLightShadows;
  updateShadows = lights.updateShadows;

  /**
   * Recalculate Shadow
   */
  recalculateShadows() {
    var size = this.boundingSphere.radius;
    var d = size;

    this.camera.near = size/100;
    this.camera.far = size*100;
    this.camera.updateProjectionMatrix();

    this.sunlight.shadow.camera.left = - d;
    this.sunlight.shadow.camera.right = d;
    this.sunlight.shadow.camera.top = d;
    this.sunlight.shadow.camera.bottom = - d;
    this.sunlight.shadow.camera.near = this.near;
    this.sunlight.shadow.camera.far = size * 10;
    this.sunlight.shadow.bias = -0.2 / size;
    this.sunlight.shadow.camera.updateProjectionMatrix();
    this.sunlight.shadow.needsUpdate = true;
  }

  /**
   * Sets Sky Position
   * @param {Number} [scale = 2048] 
   */
  setSkyPosition(scale = 2048) {
    var scope = this;

    this.updatePhi();
    this.updateTheta();
    let t = new THREE.Object3D();

    if (this.boundingSphere != null) {
      scale = this.boundingSphere.radius * 1.1;
      t.position.set(this.boundingSphere.center.x, this.boundingSphere.center.y, this.boundingSphere.center.z);
    }

    scope.sun.x = Math.sin(this.phi) * Math.sin(this.theta);
    scope.sun.y = Math.sin(this.phi) * Math.cos(this.theta);
    scope.sun.z = Math.cos(this.phi);
    scope.uniforms["sunPosition"].value.copy(this.sun);

    scope.sunlight.position.set(Math.sin(scope.phi) * Math.sin(scope.theta) * scale + t.position.x, Math.sin(scope.phi) * Math.cos(scope.theta) * scale + t.position.y, Math.cos(scope.phi) * scale + t.position.z);
    scope.sunlight.target = t;
    scope.sunlight.target.updateMatrixWorld();

    this.sunlight.shadow.camera.updateProjectionMatrix();
    scope.sunlight.shadow.needsUpdate = true;
  }

  /**
   * Sets fog
   */
  setFog(){
    var scope = this;

    var length = 1;
    var x = 0;
    var y = 0;
    var z = 0;

    if (scope.boundingSphere !== null) {
      length = scope.boundingSphere.radius;
      x = scope.boundingSphere.center.x;
      y = scope.boundingSphere.center.y;
      z = scope.boundingBox.min.z;
    }
    
  }

  // GROUND 
  createDefaultGround = ground.createDefaultGround;
  setSceneGround = ground.setSceneGround;
  addGround =  ground.addGround;
  updateGround =  ground.updateGround;
  updateGroundLevel = ground.updateGroundLevel;

  /**
   * Gets Bounding Box - while adding elements the bounding box already calculated. 
   * @returns {THREE.Box3} bounding box
   */
  getBoundingBox() {
    var scope = this;
    var box = new THREE.Box3();
    scope.elementsMap.forEach((child, name)=>{
      if (child instanceof THREE.Mesh && child.parent) {
        box.expandByObject(child.parent);
      }
    })

    return box;
  }


  /**
   * Clears all loaded models at the scene
   * @returns {Object} Return success or error messages  
   */
  clearAll() {
    var scope = this;
    try {
      if (scope.scene.children != undefined || scope.scene.children != null) {
        scope.scene.children.forEach((child) => {
          if (child instanceof THREE.Object3D || child instanceof THREE.Mesh)
            scope.scene.remove(child);
        });
        //viewerMgrInstance.render();
        var returnValue = {
          "status": "success",
          "message": "Viewer is cleared successfully."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Viewer is empty."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "System Error: " + err.message
      }
      return returnValue;
    }
  }

 
  /**
   * Sets Scene Fog
   * @param {boolean} active 
   * @param {Number} intensity 
   * @returns {Object} Return success or error messages 
   */
  setSceneFog(active, intensity) {
    var scope = this;
    try {
      if (scope.scene.fog != undefined) {
        if (scope.scene.fog) scope.scene.fog = null;
        var sceneFog = new THREE.Fog(scope.backgroundColor, intensity, scope.camera.position.y); // 0xefd1b5
        if (active) scope.scene.fog = sceneFog;
        else scope.scene.fog = null;
        //viewerMgrInstance.render();
        var returnValue = {
          "status": "success",
          "message": "Fog is added in viewer successfully."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Fog is undefined."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "System Error:" + err.message
      }
      return returnValue;
    }
  }

  /**
   * multiple or single element selection from user data i.e true or false
   * @param {boolean} status 
   * @returns {Object} Return success or error messages 
   */
  multiSelection(status) {
    var scope = this;
    try {
      if (status != undefined || status != null) {
        scope.multipleSelectionFlagCheck = status;
        scope.clearPickSelection();
        //viewerMgrInstance.render();
        var returnValue = {
          "status": "success",
          "message": "Multiple selection enabled successfully."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Status value is either undefined or null."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "System Error :" + err.message
      }
      return returnValue;
    }
  }

 
  /**
   * Sets up raycast
   * @param {Event} event 
   */
  raycastSetup(event) {
    var scope = this;
    const rendererContext = scope.renderer.getContext();
    var canvasBounds = rendererContext.canvas.getBoundingClientRect();
    scope.mouse.x = ((event.clientX - canvasBounds.left) / (canvasBounds.right - canvasBounds.left)) * 2 - 1;
    scope.mouse.y = -((event.clientY - canvasBounds.top) / (canvasBounds.bottom - canvasBounds.top)) * 2 + 1;
    scope.raycaster = new THREE.Raycaster();
    scope.raycaster.layers.set(1);
    scope.raycaster.setFromCamera(scope.mouse, scope.camera);
    // viewerMgrInstance.render();
  }
  

  // █████ █████ █     █████  ████ █████ █████  ███  █   █ 
  // █     █     █     █     █       █     █   █   █ ██  █ 
  // █████ ████  █     ████  █       █     █   █   █ █ █ █ 
  //     █ █     █     █     █       █     █   █   █ █  ██ 
  // █████ █████ █████ █████  ████   █   █████  ███  █   █ 

  pickElement = selecTion.pickElement;
  applySelectionMaterial = selecTion.applySelectionMaterial;
  clearPickSelection = selecTion.clearPickSelection;
  getSelected = selecTion.getSelected;
  clearSelection = selecTion.clearSelection;
  selectElement = selecTion.selectSingleElement;
  selectElements = selecTion.selectMultipleElements;

  // █   █ █████  ████ █   █ █     █████  ████ █   █ █████ 
  // █   █   █   █     █   █ █       █   █     █   █   █   
  // █████   █   █  ██ █████ █       █   █  ██ █████   █   
  // █   █   █   █   █ █   █ █       █   █   █ █   █   █   
  // █   █ █████  ███  █   █ █████ █████  ███  █   █   █   


  highlightElement = highLight.highlightElement;
  applyHighlightMaterial = highLight.applyHighlightMaterial;
  applyHighlightMaterials = highLight.applyHighlightMaterials;
  clearHighlights = highLight.clearHighlights;
  highlightedElement = highLight.highlightedElement;
  highlightCheck = highLight.highlightCheck;

  // █████  ███   ███  █     █████ █████ ████  
  //   █   █   █ █   █ █       █     █   █   █ 
  //   █   █   █ █   █ █       █     █   ████  
  //   █   █   █ █   █ █       █     █   █     
  //   █    ███   ███  █████   █   █████ █     


  setTooltipContent = toolTip.setTooltipContent;
  formatTooltipDefault = toolTip.formatTooltipDefault;
  formatTooltipMove = toolTip.formatTooltipMove;
  applyToolTip = toolTip.applyToolTip;
  formatTooltipOn = toolTip.formatTooltipOn;
  formatTooltipOff = toolTip.formatTooltipOff

  // █████ ████   ████ █████ █████ 
  // █     █   █ █     █     █     
  // ████  █   █ █  ██ ████  █████ 
  // █     █   █ █   █ █         █ 
  // █████ ████   ███  █████ █████ 


  /**
   * Adds or remove edges
   * @param {boolean} status 
   */
  updateEdges(status) {
    var scope = this;
    if(status != undefined) scope.showEdges = status;
    
    if (scope.edgesMap.size>0) {
      if (scope.showEdges == true) {
        scope.scene.add(scope.edgesGroup);
      }
      else {
        scope.scene.remove(scope.edgesGroup);
      }
    }
  }


  //  ███  █   █ █████ █     █████ █   █ █████ 
  // █   █ █   █   █   █       █   ██  █ █     
  // █   █ █   █   █   █       █   █ █ █ ████  
  // █   █ █   █   █   █       █   █  ██ █     
  //  ███  █████   █   █████ █████ █   █ █████ 


 
  /**
   * Edge Visibility for single and multiple elements
   * @param {Object} entity 
   */
  setOutlinePass(entity) {
    var scope = this;
    scope.lastSelectedElement = entity;
    scope.renderPass = new RenderPass(scope.scene, scope.camera);
    scope.composer.addPass(scope.renderPass);
    scope.outlinePass = new OutlinePass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      scope.scene,
      scope.camera
    );
    scope.outlinePass.visibleEdgeColor = new THREE.Color(1, 0, 0);
    scope.outlinePass.pulsePeriod = 0;
    scope.outlinePass.edgeGlow = 1;
    scope.outlinePass.edgeThickness = 1;
    scope.outlinePass.edgeStrength = 3;
    scope.composer.addPass(scope.outlinePass);
    scope.outlinePass.selectedObjects = [scope.lastSelectedElement];
    //viewerMgrInstance.render();
  }

  // █   █ █████ █████ █████ ████  █████ █     █████ █████ █   █ 
  // █   █   █   █       █   █   █   █   █       █     █    █ █  
  //  █ █    █   █████   █   ████    █   █       █     █     █   
  //  █ █    █       █   █   █   █   █   █       █     █     █   
  //   █   █████ █████ █████ ████  █████ █████ █████   █     █   

  showAll = visibiliTy.showAll;
  showElement = visibiliTy.showElement;
  showElements = visibiliTy.showElements;
  isolateElement = visibiliTy.isolateElement;
  isolateElements = visibiliTy.isolateElements;
  isObjectVisible = visibiliTy.isObjectVisible;
  fadeElement = visibiliTy.fadeElement;
  fadeElements = visibiliTy.fadeElements;
  
  // Expose restore methods
  restoreMaterial = visibiliTy.restoreMaterial;
  restoreElements = visibiliTy.restoreElements;

  // █      ███   ████ █  █  

  // █     █   █ █     █ █   
  // █     █   █ █     ██ █  
  // █     █   █ █     █  █  
  // █████  ███   ████ █   █ 

  lockElement = visibiliTy.lockElement;
  lockElements = visibiliTy.lockElements;

  // █      ███  ████  █████ █     
  // █     █   █ █   █ █     █     
  // █     █████ ████  ████  █     
  // █     █   █ █   █ █     █     
  // █████ █   █ ████  █████ █████ 


  labelElements = laBel.labelElements;
  labelElement = laBel.labelElement;
  makeInstance = laBel.makeInstance;
  labelUpdate = laBel.labelUpdate;
  updateAllLabels = laBel.updateAllLabels;
  clearAllLabels = laBel.clearAllLabels;
  removeLabel = laBel.removeLabel;
  removeLabels = laBel.removeLabels;
  clipLabels = laBel.clipLabels;
  restoreLabels = laBel.restoreLabels;

//   ████  ███  █   █ █████ ████   ███  
//  █     █   █ ██ ██ █     █   █ █   █ 
//  █     █████ █ █ █ ████  ████  █████ 
//  █     █   █ █   █ █     █   █ █   █ 
//   ████ █   █ █   █ █████ █   █ █   █ 
  
//  █▀▀ █▀▀ ▀█▀ 
//  ▄██ ██▄  █  

  setNamedCamera = cameras.setNamedCamera;
  setCamera = cameras.setCamera;
  setCenteredCamera = cameras.setCenteredCamera;
  getCameraCoordinates = cameras.getCameraCoordinates;
  setPerspectiveCamera =  cameras.setPerspectiveCamera;
  setCameraPosition = cameras.setCameraPosition;
  getCurrentCamera = cameras.getCurrentCamera;
  getCurrentCameraAttributes = cameras.getCurrentCameraAttributes
  
  //  █████  ███   ███  █   █ 
  //     █  █   █ █   █ ██ ██ 
  //    █   █   █ █   █ █ █ █ 
  //   █    █   █ █   █ █   █ 
  //  █████  ███   ███  █   █ 

  zoomToElements = cameras.zoomToElements;
  zoomSelection = cameras.zoomSelection;
  setBounds = cameras.setBounds;
  zoomExtents = cameras.zoomExtents;
  zoomTo = cameras.zoomTo;
  zoomByPercentage = cameras.zoomByPercentage;


  //  ███  ████   ███   ████ █████ █████ █   █ 
  // █   █ █   █ █   █ █       █     █    █ █  
  // █   █ ████  █████ █       █     █     █   
  // █   █ █     █   █ █       █     █     █   
  //  ███  █     █   █  ████ █████   █     █   

  setElementOpacity = visibiliTy.setElementOpacity;
  setOpacity = visibiliTy.setOpacity;

  //  ████ █   █  ███  █████ █████ 
  // █     █   █ █   █ █       █   
  // █  ██ █████ █   █ █████   █   
  // █   █ █   █ █   █     █   █   
  //  ███  █   █  ███  █████   █   

  ghostElement = visibiliTy.ghostElement;
  ghostElements = visibiliTy.ghostElements;

  //  ████  ███  █      ███  ████     ████  █   █ 
  // █     █   █ █     █   █ █   █    █   █  █ █  
  // █     █   █ █     █   █ ████     ████    █   
  // █     █   █ █     █   █ █   █    █   █   █   
  //  ████  ███  █████  ███  █   █    ████    █   

  colorElement = colorBy.colorElement;
  resetColor = colorBy.resetColor;
  resetAllColors = colorBy.resetAllColors;
  colorElements = colorBy.colorElements;
 
  resetElement = colorBy.resetElement;
  
  // Clear all colorBy overrides
  clearColorBy = colorBy.clearColorBy;
  removeFilterEffects = colorBy.removeFilterEffects;

  setSceneBackground = colorBy.setSceneBackground;
  setHighlightColor = colorBy.setHighlightColor;
  setSelectionColor = colorBy.setSelectionColor;

  // DefaultMaterials
  setDefaultMaterials = materials.setDefaultMaterials;
  setGhostMaterial = materials.setGhostMaterial;
  setSelectionMaterial = materials.setSelectionMaterial;
  setHighlightMaterial = materials.setHighlightMaterial;
  setGroundMaterial =  materials.setGroundMaterial;
  
  // onCanvasResize
  /**
   * On Canvas Size
   */
  onCanvasResize() {
    var scope = this;
    if (scope != undefined && scope.canvas != undefined) {
      scope.camera.aspect = scope.canvas.offsetWidth / scope.canvas.offsetHeight;
      scope.camera.updateProjectionMatrix();
      scope.renderer.setSize(scope.canvas.offsetWidth, scope.canvas.offsetHeight);
      scope.renderer.render(scope.scene, scope.camera);
      scope.labelRenderer.setSize(scope.canvas.offsetWidth, scope.canvas.offsetHeight);
      scope.labelRenderer.render(scope.scene, scope.camera);
    }
  }

 // Clipping
  updateClipping = clipping.updateClipping;
  setClippingAxis = clipping.setClippingAxis;
  setClippingDirection = clipping.setClippingDirection;
  addClippingPlanes = clipping.addClippingPlanes;
  addClippingPlane = clipping.addClippingPlane;
  createStencil = clipping.createStencil;


  /**
   * Clean up the instance
   * @name destroy
   */
  destroy() {
    var scope = this;
    if (scope.resizeObserver) {
      scope.resizeObserver.disconnect();
    }
    scope.canvas.innerHTML = "";
  }

  parseColor = utils.parseColor;
  toHex = utils.toHex;
  toObjString = utils.toObjString;
  setDigits = utils.setDigits;

  /**
   * Checks whether Is Object
   * @param {any} objValue 
   * @returns {boolean} Return true or false
   */
  isObject(objValue) {
    return objValue && typeof objValue === 'object' && objValue.constructor === Object;
  }
  
  /**
   * Update Tween
   */
  updateTween(){
   TWEEN.update();
  }
}

export { Viewer };